import type { ChordDestination } from '../hooks/useGlobalShortcuts';
import type { ProjectListItem } from '../api/types';

// Spec/12 §12.9 — Vim-style navigation chord targets. The chords were
// armed in `useGlobalShortcuts`; this resolver decides where each chord
// actually navigates given the current active project.
//
// Path-type truthfulness (Phase 4 correction): chord destinations
// respect the same path-type visibility rules the sidebar enforces in
// `Sidebar.tsx`. Routing G+C/G/A/I to a per-project URL while the
// active project is HTML would land on a page the visible nav
// intentionally hides for that project (and would NOT be caught by
// `ProjectScopedRedirect`, which only wraps the flat `/csdb` etc.
// routes — not the per-project `/projects/:id/csdb` routes the chord
// targets). Same story for G+M on an active S1000D project. The
// truthful response is to navigate to a destination that matches what
// the user would actually see in the sidebar today.
//
//   • `G then D` → /projects/:id/dashboard when an active project
//                  exists; otherwise /projects (Home). The old global
//                  `/dashboard` route now redirects to /projects, so a
//                  Home-bound fallback is the truthful destination
//                  when no project context is set.
//   • `G then P` → /projects (Home).
//   • `G then R` → /review (always — flat picker, always visible).
//   • `G then M` (HTML Manuals chord):
//        active HTML project → /projects/:id/manuals
//        otherwise           → /manuals (the flat picker — also where
//                              the sidebar links when no active project
//                              biases visibility; this is a real page
//                              and a truthful destination for the
//                              "manuals" chord on any caller).
//   • `G then C/G/A/I` (Path A per-project surfaces):
//        active S1000D project → /projects/:id/{csdb|graphics|
//                                applicability|import}
//        otherwise             → /projects (truthful fallback to the
//                                project list, matching the sidebar's
//                                "no per-project tools without an
//                                S1000D project" stance).
//
// Lives in its own module so `Header.tsx` can stay a pure
// component-export file (react-refresh strict mode).
export function chordTarget(
  dest: ChordDestination,
  activeProject: ProjectListItem | null,
): string {
  switch (dest) {
    case 'dashboard':
      if (activeProject) {
        return `/projects/${activeProject.id}/dashboard`;
      }
      return '/projects';
    case 'projects':
      return '/projects';
    case 'review':
      return '/review';
    case 'manuals':
      if (activeProject && activeProject.pathType === 'html') {
        return `/projects/${activeProject.id}/manuals`;
      }
      return '/manuals';
    case 'csdb':
      if (activeProject && activeProject.pathType === 's1000d') {
        return `/projects/${activeProject.id}/csdb`;
      }
      return '/projects';
    case 'graphics':
      if (activeProject && activeProject.pathType === 's1000d') {
        return `/projects/${activeProject.id}/graphics`;
      }
      return '/projects';
    case 'applicability':
      if (activeProject && activeProject.pathType === 's1000d') {
        return `/projects/${activeProject.id}/applicability`;
      }
      return '/projects';
    case 'import':
      if (activeProject && activeProject.pathType === 's1000d') {
        return `/projects/${activeProject.id}/import`;
      }
      return '/projects';
  }
}
